import type { TextureManager } from '../texture-manager.class';
import { setEnvironmentTextureCfn } from './set-environment-texture.cfn';

export interface SetEnvironmentTextureAsyncParams {
	key: string;
	onProgress?: (completed: number, total: number) => void;
}

/**
 * Update environment texture for reflections without blocking the UI.
 * Uses pre-loaded PMREM and incremental material updates.
 *
 * Falls back to synchronous version if PMREM is not pre-loaded.
 */
export const setEnvironmentTextureAsyncCfn = async (
	self: TextureManager,
	params: SetEnvironmentTextureAsyncParams
): Promise<boolean> => {
	const { key, onProgress } = params;
	const preloader = self.pmremPreloader;
	const materialUpdater = self.materialUpdater;
	const materials = self.materials;

	// Check if PMREM is pre-generated
	let envMap = preloader?.getPreloaded(key);

	if (!envMap) {
		console.warn(
			'[setEnvironmentTextureAsync] PMREM not pre-loaded for key:',
			key,
			'- falling back to sync version'
		);
		// Fall back to synchronous version
		return setEnvironmentTextureCfn(self, { key });
	}

	// Dispose previous PMREM envMap if it exists
	const currentEnvMap = self.environmentMap;
	if (currentEnvMap && (currentEnvMap as any).isRenderTargetTexture) {
		try {
			currentEnvMap.dispose();
		} catch (e) {
			console.warn('Error disposing previous PMREM envMap:', e);
		}
	}

	self.environmentMap = envMap;

	// Update materials incrementally if updater is available
	if (materialUpdater && materials.size > 0) {
		await materialUpdater.updateMaterials({
			materials,
			envMap,
			batchSize: 5,
			onProgress
		});
	} else {
		// Fallback: update all materials synchronously
		for (const material of materials.values()) {
			material.envMap = envMap;
			material.needsUpdate = true;
		}
	}

	return true;
};
